import { Injectable } from '@angular/core';
import { io, Socket } from 'socket.io-client';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { filter } from 'rxjs/operators';
import { environment } from '../../../../environments/environment';
import { AuthService } from '../../../core/auth/services/auth.service';
import { CoachMessage, CoachProductLine, WhatsAppThreadState } from './coaching.service';

export interface CoachingMessageEvent {
  thread_id: string;
  assignment_id?: string;
  product_line?: CoachProductLine;
  message: CoachMessage;
  /** Optional thread WhatsApp state (added by the API in S7; may be partial or absent). */
  whatsapp?: Partial<WhatsAppThreadState> | null;
}

/** Optional delivery update for a message already shown (API S7). */
export interface CoachingMessageStatusEvent {
  thread_id: string;
  message_id: string;
  status: string;
  error?: string | null;
}

export interface CoachingNewClientEvent {
  assignment_id: string;
  user_id: string;
  product_line: CoachProductLine;
  thread_id: string | null;
}

export interface CoachingUnknownWhatsAppEvent {
  id: string;
  phone_digits: string;
  display_number: string;
  product_line: CoachProductLine;
  content: string;
  sent_at?: string | null;
  external_id: string;
}

const AUTH_RETRY_BASE_MS = 3000;
const AUTH_RETRY_MAX = 5;

/**
 * Socket.IO client for coaching realtime events.
 * Backend joins every authenticated socket to `user:{id}` and emits
 * `coaching:message` / `coaching:new_client` there.
 */
@Injectable({ providedIn: 'root' })
export class CoachingSocketService {
  private socket: Socket | null = null;
  private hasConnectedOnce = false;
  private authRetries = 0;
  private authRetryTimer?: ReturnType<typeof setTimeout>;
  private readonly connectedSubject = new BehaviorSubject<boolean>(false);
  private readonly reconnectedSubject = new Subject<void>();
  private readonly messageSubject = new Subject<CoachingMessageEvent>();
  private readonly messageStatusSubject = new Subject<CoachingMessageStatusEvent>();
  private readonly newClientSubject = new Subject<CoachingNewClientEvent>();
  private readonly unknownSubject = new Subject<CoachingUnknownWhatsAppEvent>();

  readonly connected$ = this.connectedSubject.asObservable();
  /** Fires on every connect after the first one — events may have been missed meanwhile. */
  readonly reconnected$ = this.reconnectedSubject.asObservable();
  readonly messages$ = this.messageSubject.asObservable();
  readonly messageStatus$ = this.messageStatusSubject.asObservable();
  readonly newClients$ = this.newClientSubject.asObservable();
  readonly unknownWhatsApp$ = this.unknownSubject.asObservable();

  constructor(private auth: AuthService) {}

  connect(): void {
    if (!this.auth.getToken()) {
      return;
    }
    if (this.socket?.connected) {
      return;
    }

    this.disconnect();

    this.socket = io(this.socketOrigin(), {
      // Callback form: re-read the token on every (re)connect attempt so a refreshed
      // or re-issued token is picked up without rebuilding the socket.
      auth: cb => cb({ token: this.auth.getToken() }),
      transports: ['websocket', 'polling'],
      reconnection: true,
      reconnectionDelay: 2000,
      reconnectionDelayMax: 15000
    });

    this.socket.on('connect', () => {
      this.connectedSubject.next(true);
      this.authRetries = 0;
      if (this.hasConnectedOnce) {
        this.reconnectedSubject.next();
      }
      this.hasConnectedOnce = true;
    });
    this.socket.on('disconnect', () => this.connectedSubject.next(false));
    this.socket.on('connect_error', (err: Error) => {
      this.connectedSubject.next(false);
      this.onConnectError(err);
    });

    this.socket.on('coaching:message', (payload: CoachingMessageEvent) => {
      if (payload?.thread_id && payload?.message) {
        this.messageSubject.next(payload);
      }
    });

    this.socket.on('coaching:message_status', (payload: CoachingMessageStatusEvent) => {
      if (payload?.thread_id && payload?.message_id && payload?.status) {
        this.messageStatusSubject.next(payload);
      }
    });

    this.socket.on('coaching:new_client', (payload: CoachingNewClientEvent) => {
      if (payload?.assignment_id) {
        this.newClientSubject.next(payload);
      }
    });

    this.socket.on('coaching:unknown_whatsapp', (payload: CoachingUnknownWhatsAppEvent) => {
      if (payload?.phone_digits) {
        this.unknownSubject.next(payload);
      }
    });
  }

  disconnect(): void {
    clearTimeout(this.authRetryTimer);
    this.authRetries = 0;
    this.hasConnectedOnce = false;
    if (this.socket) {
      this.socket.removeAllListeners();
      this.socket.disconnect();
      this.socket = null;
    }
    this.connectedSubject.next(false);
  }

  get isConnected(): boolean {
    return !!this.socket?.connected;
  }

  messagesForThread(threadId: string): Observable<CoachingMessageEvent> {
    return this.messages$.pipe(filter(e => e.thread_id === threadId));
  }

  /**
   * Socket.IO does not retry by itself when the server's middleware rejects the
   * handshake. For auth failures, retry with backoff — the auth callback re-reads
   * the current token each time — and give up while signed out.
   */
  private onConnectError(err: Error): void {
    const socket = this.socket;
    if (!socket || socket.active) {
      return; // transport error: the built-in reconnection handles it
    }
    const message = String(err?.message || '');
    if (!/auth|token|jwt|unauthori[sz]ed|forbidden|expired/i.test(message)) {
      return;
    }
    if (!this.auth.getToken() || this.authRetries >= AUTH_RETRY_MAX) {
      return;
    }
    const delay = AUTH_RETRY_BASE_MS * 2 ** this.authRetries;
    this.authRetries += 1;
    clearTimeout(this.authRetryTimer);
    this.authRetryTimer = setTimeout(() => {
      if (this.socket === socket && !socket.connected && this.auth.getToken()) {
        socket.connect();
      }
    }, delay);
  }

  /** API is `http://host/api` or `http://host/api/v1` — Socket.IO lives on the host origin. */
  private socketOrigin(): string {
    try {
      const url = new URL(environment.api, window.location.origin);
      return url.origin;
    } catch {
      return window.location.origin;
    }
  }
}
